<template>
  <div class="reset menu">
    <div class="menuBox__container" data-testid="container_userMainMenu" @click="openMenu">
      <div class="menuBox__greeting t3 -t_semi-bold ">
        <sword-button-wuk
          v-if="showAccountMenu"
          class="menu-btn t3 -t_semi-bold hidden-mobile"
          theme="text-heavy"
          :text="$t('eco_account')"
          data-testid="text_memberName"
        />
        <sword-button-wuk
          v-else
          class="menu-btn t3 -t_semi-bold hidden-mobile"
          theme="text-heavy"
          :text="$t('eco_login_log-in')"
          data-testid="text_memberName"
          @click.native.stop="goToLogin"
        />
        <sword-button-wuk
          class="menu-btn t3 -t_semi-bold show-mobile"
          icon="menu"
          theme="text-heavy"
        />
      </div>
    </div>

    <div v-show="isMenuOpen"
         class="menu__backdrop"
         @click="closeMenu"
    >
    </div>
    <transition name="fade">
      <nav v-show="isMenuOpen" class="menuDropdown__nav" data-testid="container_menuOptionsDropdown">
        <transition name="slide-2">
          <language-selector-list
            v-if="showLanguageMenu"
            class="animate__animated"
            :selected="langOptions.selected"
            :options="langOptions.list"
            @selected="handleSelectLang"
          >
            <template #title>
              <p class="menuDropdown__title -t_semi-bold" @click="() => showLanguageMenu = false">
                <sword-icon-wuk name="chevron-down" class="chevron__left" />
                <span>
                  {{ $t("select_a_language") }}
                </span>
              </p>
            </template>
          </language-selector-list>
        </transition>
        <transition name="slide-1">
          <div v-if="!showLanguageMenu" class="animate__animated">
            <p class="menuDropdown__title -t_semi-bold">
              {{ $t('eco_account') }}
            </p>
            <ul class="menuDropdown__list">
              <li v-if="showLanguageOption" class="menuDropdown__listItem --lang-option show-mobile" @click="() => showLanguageMenu = true">
                <p class="menuDropdown__flagItem">
                  <language-flag class="flag -mr_1" :value="langOptions.selected" />
                  <span>{{ langCopy }}</span>
                </p>
                <sword-icon-wuk name="chevron-down" class="chevron__right" />
              </li>
              <li v-for="option in menuOptions"
                  :key="option.label"
                  class="menuDropdown__listItem"
                  :data-testid="`button_menuOption_${option.name}`"
                  @click="option.action"
              >
                <sword-icon-wuk :name="option.icon" />
                <p>{{ $t(option.label) }}</p>
              </li>
            </ul>
          </div>
        </transition>
      </nav>
    </transition>
  </div>
</template>

<script>
import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';
import SwordButtonWuk from '@ui-kit/components/buttons/SwordButton.vue';
import { AVAILABLE_COUNTRIES_FOR_LANGUAGES } from '@/scripts/constants/languages';
import LanguageSelectorList from './LanguageSelectorList.vue';
import LanguageFlag from './LanguageFlag.vue';


export default {
  name: 'MainMenu',
  components: {
    SwordIconWuk,
    LanguageSelectorList,
    LanguageFlag,
    SwordButtonWuk,
  },

  props: {
    member: {
      type: Object,
      required: true,
    },
    langOptions: {
      type: Object,
      default: () => ({ list: [], selected: '' }),
    },
    showLanguageOption: {
      type: Boolean,
      default: false,
    },
    isLoggedIn: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['logout', 'login', 'goToDeleteAccount', 'lang:selected'],

  data() {
    return { isMenuOpen: false, showLanguageMenu: false };
  },

  computed: {
    showAccountMenu() {
      const dontShowAccountRoutes = ['Login', 'LockedLogin', 'PasswordRecovery', 'PasswordRecoverySuccess'];

      return this.isLoggedIn && !dontShowAccountRoutes.includes(this.$route.name);
    },
    menuOptions() {
      const isLoginPage = this.$route.name === 'Login';
      const isAuthRoute = ['Login', 'LockedLogin', 'PasswordRecovery', 'PasswordRecoverySuccess'].includes(this.$route.name);

      if (isLoginPage) return [];

      if (!this.isLoggedIn && !isLoginPage) {
        return [
          {
            name: 'login',
            icon: 'login',
            label: 'eco_login_log-in',
            action: () => {
              this.$emit('login');
              this.closeMenu();
            },
          },
        ];
      }

      if (!isAuthRoute) {
        return [
          {
            name: 'logout',
            icon: 'logout',
            label: 'onboarding2_logout',
            action: () => {
              this.$emit('logout');
              this.closeMenu();
            },
          },
          {
            name: 'delete',
            icon: 'delete',
            label: 'onboarding2_delete_account',
            action: () => {
              this.$emit('goToDeleteAccount');
              this.closeMenu();
            },
          },
        ];
      }

      return [];
    },
    langCopy() {
      const country = AVAILABLE_COUNTRIES_FOR_LANGUAGES.find(c => c.language === this.langOptions.selected);
      return country ? this.$t(country.selectionTranslationKey) : this.langOptions.selected;
    },
  },

  methods: {
    openMenu() {
      this.isMenuOpen = true;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    handleSelectLang(lang) {
      this.$emit('lang:selected', lang);
      this.showLanguageMenu = false;
    },
    goToLogin() {
      this.$emit('login');
    },
  },
};
</script>

<style lang="scss" scoped>
.slide-1-leave-to,
.slide-2-leave-to {
  display: none;
}

.slide-2-leave-active {
  animation: fadeOut;
  animation-duration: .1s;
}
.slide-2-enter-active {
  animation: fadeInRight;
  animation-duration: .2s;
}
.slide-1-enter-active {
  animation: fadeInLeft;
  animation-duration: .2s;
}
.slide-1-leave-active {
  animation: fadeOut;
  animation-duration: .1s;
}

.menu {
  @media screen and (min-width: $tablet-size) {
    position: relative;
  }

  &__backdrop {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 999;
    opacity: 0.3;
    background-color: getColor($greys, 1);

    @media screen and (min-width: $tablet-size) {
      background-color: transparent;
    }
  }
}

.menuBox {
  &__container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: map-get($sh-spacers-wuk, 2);
    cursor: pointer;
  }
}

.menuDropdown {
  &__nav {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 999999;

    border-radius: 1.5rem 1.5rem 0px 0px;
    background-color: #fff;
    box-shadow: 0px 2px 6px rgba(29, 56, 97, 0.25);

    @media screen and (min-width: $tablet-size) {
      position: absolute;
      bottom: auto;
      right: 0;
      left: auto;
      min-width: 17.5rem;
      margin-top: map-get($sh-spacers-wuk, 2);
      padding: 1rem 0;
      border-radius: 1rem;
    }

    @media screen and (max-width: $tablet-size) {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      overflow-x: hidden;
      padding-bottom: 1.5rem;

      .animate__animated {
        flex-shrink: 0;
        width: 100%;
      }
    }

    &::v-deep {
      .chevron__left {
        transform: rotate(90deg);
        margin-right: 1rem;
      }
    }
  }

  &__title {
    @extend %t1;
    @extend %-t_semi-bold;
    display: flex;
    align-items: center;
    padding: map-get($sh-spacers-wuk, 3) map-get($sh-spacers-wuk, 2);

    @media screen and (min-width: $tablet-size) {
      display: none;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__listItem {
    @extend %t3;
    @extend %-t_medium;
    display: flex;
    align-items: center;
    gap: .5rem;
    margin: 0.75rem map-get($sh-spacers-wuk, 2);
    cursor: pointer;

    &::v-deep {
      .sword-icon {
        &.svg-icon {
          height: 24px;
          width: 24px;
        }

        &.chevron__right {
          transform: rotate(-90deg);
        }
      }
    }

    &.--lang-option {
      justify-content: space-between;
    }

    @media screen and (min-width: $tablet-size) {
      margin: 0.875rem 1rem;

      &:hover {
        color: $secondary-color;
      }
    }
  }

  &__flagItem {
    display: flex;
    align-items: center;
  }
}

@media screen and (max-width: $tablet-size) {
  .fade-leave-active {
    animation: fadeOutDown;
    animation-duration: .2s;
  }
  .fade-enter-active {
    animation: slideInUp;
    animation-duration: .2s;
  }
}

@media screen and (min-width: $tablet-size) {
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
}
</style>
